import { fieldValidationRulesLite } from '@ingka-group-digital/edve-field-validation-rules-lite';
import { getFieldValidationRulesLite } from '@ingka-group-digital/edve-validation-utils';
import { captureSentry } from 'Utils/sentry';
import FieldKeyConstants from '../fieldKeyConstants';

// Shared EDVE v2 validation rule helpers

const reportedMissingPrimitiveIds = new Set();

const createRegExpFromPrimitive = (primitive, forceIgnoreCase) => {
  const pattern = primitive?.patterns?.[0]?.pattern;
  if (!pattern) {
    return null;
  }
  const ignoreCase = forceIgnoreCase ?? primitive?.flags?.ignoreCase;
  const unicodeAware = primitive?.flags?.unicodeAware;
  const flags = `${ignoreCase ? 'i' : ''}${unicodeAware ? 'u' : ''}`;
  return new RegExp(pattern, flags);
};

const reportMissingPrimitive = primitiveId => {
  if (reportedMissingPrimitiveIds.has(primitiveId)) {
    return;
  }

  reportedMissingPrimitiveIds.add(primitiveId);
  captureSentry(new Error(`Missing EDVE primitive: ${primitiveId}`), {
    primitiveId
  });
};

const resolvePrimitiveRegExp = (primitiveId, primitives) => {
  const resolvedPrimitive = primitives.find(
    primitive => primitive.id === primitiveId
  );
  if (resolvedPrimitive?.status !== 'OK' || !resolvedPrimitive.primitive) {
    reportMissingPrimitive(primitiveId);
    return null;
  }

  const regexp = createRegExpFromPrimitive(resolvedPrimitive.primitive);
  if (!regexp) {
    reportMissingPrimitive(primitiveId);
  }
  return regexp;
};

const getPrimitiveRegExp = (primitiveId, forceIgnoreCase) => {
  const primitive = fieldValidationRulesLite.primitives[primitiveId];
  const regexp = createRegExpFromPrimitive(primitive, forceIgnoreCase);
  if (!regexp) {
    reportMissingPrimitive(primitiveId);
  }
  return regexp;
};

const resolveFieldPolicyRegExps = (fieldKey, countryCode) => {
  if (!fieldKey) {
    return null;
  }
  const { policies, primitives } = getFieldValidationRulesLite(
    fieldValidationRulesLite,
    { retailMarketFilter: [countryCode], fieldFilter: [fieldKey] }
  );
  const policy = policies[0]?.policy;
  if (!policy) {
    return null;
  }
  const primitiveIds = [
    ...new Set([
      ...(policy.primitiveIds ?? []),
      ...(policy.fallback?.primitiveIds ?? [])
    ])
  ];
  const regExps = primitiveIds.map(id =>
    resolvePrimitiveRegExp(id, primitives)
  );
  if (regExps.some(regExp => !regExp)) {
    return null;
  }
  return {
    compositionType: policy.compositionType,
    constraints: policy.constraints,
    presence: policy.presence,
    regExps
  };
};

const matchesFieldPolicy = (value, resolvedPolicy) => {
  if (!resolvedPolicy || !resolvedPolicy.regExps.length) {
    return false;
  }
  const { compositionType, regExps } = resolvedPolicy;
  return compositionType === 'ANY_OF'
    ? regExps.some(re => re.test(value))
    : regExps.every(re => re.test(value));
};

const CONSOLIDATED_V2_FIELD_OVERRIDES = {
  SSN_NUMBER: 'GOVERNMENT_ID',
  FISCAL_CODE: 'GOVERNMENT_ID',
  TAX_CODE: 'GOVERNMENT_ID'
};

const FIELD_KEY_ALIASES = {
  email: 'EMAIL',
  addressCountryCode: 'COUNTRY_CODE',
  country: 'COUNTRY_CODE'
};

const getFieldKeyByLabel = value => {
  const fieldName = value?.replace(/^forms\./, '');

  if (FIELD_KEY_ALIASES[fieldName]) {
    return FIELD_KEY_ALIASES[fieldName];
  }
  const fieldKey = Object.keys(FieldKeyConstants).find(
    key => FieldKeyConstants[key] === fieldName
  );
  return CONSOLIDATED_V2_FIELD_OVERRIDES[fieldKey] ?? fieldKey;
};

export {
  getFieldKeyByLabel,
  getPrimitiveRegExp,
  matchesFieldPolicy,
  resolveFieldPolicyRegExps
};
